// src/pages/PublicEvents.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { getCompetitionsSummary } from "../firebase/competitions";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { useTheme } from "../context/ThemeContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PublicHeader from "../components/PublicHeader";
import { C, FONT } from "../theme";
import { formatDateGeorgian } from "../utils/dateHelpers";

// ── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface Competition {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  earlyDeadline?: string;
  midDeadline?: string;
  weightChangeDeadline?: string;
  earlyPrice?: number;
  midPrice?: number;
  latePrice?: number;
  status?: string;
  imageUrl?: string;
  documentUrl?: string;
  documentName?: string;
  types?: string[];
  maleCategories?: string[];
  femaleCategories?: string[];
}

function toDateStr(val: any): string {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val?.toDate === "function") return val.toDate().toISOString();
  if (typeof val?.seconds === "number") return new Date(val.seconds * 1000).toISOString();
  return String(val);
}

function currentPrice(comp: Competition): number {
  const now = new Date();
  if (comp.earlyDeadline && now <= new Date(comp.earlyDeadline)) return comp.earlyPrice ?? comp.latePrice ?? 0;
  if (comp.midDeadline && now <= new Date(comp.midDeadline)) return comp.midPrice ?? comp.latePrice ?? 0;
  return comp.latePrice ?? 0;
}

function fmt(dateStr: string) {
  if (!dateStr) return "—";
  const locale = localStorage.getItem("language") || "en";
  if (locale === "ka") {
    return formatDateGeorgian(new Date(dateStr));
  }
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function isOpen(comp: Competition) {
  if (comp.status === "closed") return false;
  if (!comp.registrationDeadline) return false;
  return new Date(comp.registrationDeadline) >= new Date();
}

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const PriceIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const ScaleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18" /><path d="M5 7h14" /><path d="m5 7-3 6a3 3 0 0 0 6 0Z" /><path d="m19 7-3 6a3 3 0 0 0 6 0Z" /><path d="M7 21h10" />
  </svg>
);
const RegisterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);
const TrophyIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5h4" /><path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
    <path d="M12 17v4" /><path d="M8 21h8" /><path d="M6 5v4a6 6 0 0 0 12 0V5H6z" />
  </svg>
);
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const KarateIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v6l-3 3" /><path d="M12 13l3 3" />
    <path d="M9 10H6" /><path d="M18 10h-3" />
  </svg>
);
const RankingsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ── Main component ─────────────────────────────────────────────────────────────
export default function PublicEvents() {
  const { currentUser, userRole, loading: authLoading, login, resetPassword } = useAuth();
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const darkTheme = resolvedTheme === "dark";
  const navigate = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;

  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loadingComps, setLoadingComps] = useState(true);
  const [tab, setTab] = useState<"open" | "past">("open");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pastPage, setPastPage] = useState(1);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingCompId, setPendingCompId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [authView, setAuthView] = useState<"login" | "forgot">("login");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Banner shown when a coach tries to register for a competition that
  // closed while the login modal was open (race between the click and
  // login completion). Cleared on dismiss.
  const [closedRegBanner, setClosedRegBanner] = useState<string | null>(null);

  useEffect(() => { const timer = setTimeout(() => setHeroLoaded(true), 80); return () => clearTimeout(timer); }, []);

  useEffect(() => {
    async function load() {
      try {
        // Lean "summary" fetch drops heavy fields (weightCategories, ranking
        // config) so this public page transfers a smaller payload — the
        // weight breakdown shown in expanded cards is informational and can
        // be re-fetched on demand if anyone needs it. Cached & persisted to
        // localStorage by firebase/competitions.ts.
        const raw = await getCompetitionsSummary();
        const data: Competition[] = (raw as any[]).map((d) => ({
          ...d,
          startDate:            toDateStr(d.startDate),
          endDate:              toDateStr(d.endDate),
          registrationDeadline: toDateStr(d.registrationDeadline),
          earlyDeadline:        toDateStr(d.earlyDeadline),
          midDeadline:          toDateStr(d.midDeadline),
          weightChangeDeadline: toDateStr(d.weightChangeDeadline),
        }));
        setCompetitions(data);
      } catch (e) {
        console.error("Failed to load competitions", e);
      } finally {
        setLoadingComps(false);
      }
    }
    load();
  }, []);

  // Re-fetches and checks competition status. Returns true if open, false
  // if closed/missing. Uses the cached getCompetitionsSummary so the cost
  // is typically free (15-min TTL, persisted to localStorage).
  const assertCompetitionOpen = async (compId: string): Promise<boolean> => {
    try {
      const raw = await getCompetitionsSummary();
      const found = (raw as any[]).find((d) => d.id === compId);
      if (!found) return false;
      // Map Timestamps → strings to match the Competition shape that isOpen() expects.
      return isOpen({
        ...found,
        registrationDeadline: toDateStr(found.registrationDeadline),
      } as Competition);
    } catch {
      // On read failure, fail open — let the register page handle it. The
      // /register/:id page has its own guards.
      return true;
    }
  };

  useEffect(() => {
    if (!authLoading && currentUser && pendingCompId) {
      const compId = pendingCompId;
      setPendingCompId(null);
      if (userRole === "admin") {
        navigate("/admin");
        return;
      }
      if (userRole !== "coach") return;
      // Race guard: the comp may have closed while the login modal was open.
      (async () => {
        const stillOpen = await assertCompetitionOpen(compId);
        if (stillOpen) {
          navigate(`/register/${compId}`);
        } else {
          const found = competitions.find((c) => c.id === compId);
          setClosedRegBanner(found?.name ?? compId);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentUser, userRole, pendingCompId, navigate]);

  // Reset search and page when tab changes
  useEffect(() => {
    setSearchQuery("");
    setPastPage(1);
  }, [tab]);

  // Reset page when search text changes
  useEffect(() => {
    setPastPage(1);
  }, [searchQuery]);

  const handleRegisterClick = (compId: string) => {
    if (currentUser) {
      if (userRole === "coach") navigate(`/register/${compId}`);
      else if (userRole === "admin") navigate("/admin");
    } else {
      setPendingCompId(compId);
      setShowLoginModal(true);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(""); setLoginLoading(true);
    try {
      const role = await login(email, password);
      setShowLoginModal(false);
      if (role === "admin") {
        navigate("/admin");
      } else if (pendingCompId) {
        // Race guard: comp may have closed between modal-open and login.
        const stillOpen = await assertCompetitionOpen(pendingCompId);
        if (stillOpen) {
          navigate(`/register/${pendingCompId}`);
        } else {
          const found = competitions.find((c) => c.id === pendingCompId);
          setClosedRegBanner(found?.name ?? pendingCompId);
          setPendingCompId(null);
        }
      } else {
        navigate("/dashboard");
      }
    } catch (err: any) {
      setLoginError(
        err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? t("login.error.invalid") : t("login.error.default")
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const switchAuthView = (view: "login" | "forgot") => {
    setAuthView(view);
    setLoginError("");
    setResetMessage("");
  };

  const closeModal = () => {
    setShowLoginModal(false); setPendingCompId(null);
    setLoginError(""); setEmail(""); setPassword("");
    setAuthView("login"); setResetMessage("");
  };

  // Forgot-password submit. Always ends on the SAME neutral message regardless
  // of whether the email is registered — never discloses account existence.
  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setResetMessage("");
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setLoginError(t("login.reset.invalidEmail"));
      return;
    }
    setResetLoading(true);
    try {
      await resetPassword(trimmed);
    } catch (err: any) {
      console.warn("Password reset request issue:", err?.code ?? err);
    } finally {
      setResetLoading(false);
      setResetMessage(t("login.reset.sent"));
    }
  };

  const openComps = competitions.filter(isOpen);
  const pastComps = competitions.filter((c) => !isOpen(c));
  const displayed = tab === "open" ? openComps : pastComps;

  // Search filter — case-insensitive match on competition name
  const filteredDisplayed = searchQuery.trim() === ""
    ? displayed
    : displayed.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // ── Pagination for Past tab ────────────────────────────────────────────────
  const PAST_PAGE_SIZE = 10;
  const pastTotalPages = tab === "past" ? Math.ceil(filteredDisplayed.length / PAST_PAGE_SIZE) : 1;
  const displayedComps = tab === "open"
    ? filteredDisplayed
    : filteredDisplayed.slice((pastPage - 1) * PAST_PAGE_SIZE, pastPage * PAST_PAGE_SIZE);

  return (
    <>
    <Helmet>
      <title>{t("seo.events.title")}</title>
      <meta name="description" content={t("seo.events.description")} />
      <link rel="canonical" href="https://registration.taekwondo.ge/events" />
      <meta property="og:title" content={t("seo.events.title")} />
      <meta property="og:description" content={t("seo.events.description")} />
      <meta property="og:url" content="https://registration.taekwondo.ge/events" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={t("seo.events.title")} />
      <meta name="twitter:description" content={t("seo.events.description")} />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href="https://registration.taekwondo.ge/events" />
      <link rel="alternate" hrefLang="ka" href="https://registration.taekwondo.ge/events" />
      <link rel="alternate" hrefLang="x-default" href="https://registration.taekwondo.ge/events" />
    </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Figtree:wght@400;500;600;700;800&display=swap');

        @keyframes heroBgZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .pe-loaded .pe-anim-0 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.08s both; }
        .pe-loaded .pe-anim-1 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.2s both; }
        .pe-loaded .pe-anim-2 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.32s both; }
        .pe-loaded .pe-anim-3 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.44s both; }

        .pe-card { transition: transform 0.25s cubic-bezier(.22,1,.36,1), box-shadow 0.25s; }
        .pe-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-raised) !important; }
        .pe-back-btn:hover { color: var(--color-text) !important; border-color: var(--color-text-subtle) !important; }
        .pe-nav-link:hover { color: var(--color-accent) !important; }
        .pe-tab-btn:hover { color: var(--color-text) !important; }
        .pe-register-btn:hover { opacity: 0.88 !important; transform: translateY(-1px) !important; }
        .pe-details-btn:hover { border-color: var(--color-text-subtle) !important; color: var(--color-text) !important; }
        .pe-nav-sign:hover { background: rgba(218,41,28,0.85) !important; }
        .pe-stat-pill:hover { background: var(--color-border-strong) !important; }
        .pe-rankings-link:hover { background: var(--color-border) !important; }
        .pe-search-input:focus { border-color: rgba(218,41,28,0.5) !important; box-shadow: 0 0 0 3px rgba(218,41,28,0.1) !important; outline: none; }
        .pe-search-input::placeholder { color: var(--color-text-subtle); }

        .pe-events-grid {
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 380px));
          justify-content: center;
          align-items: start;
        }
        .pe-card__meta-value { min-width: 0; overflow-wrap: anywhere; }
        .pe-card__actions > button { min-width: 0; }
        @media (max-width: 480px) {
          .pe-mobile-actions { align-items: stretch !important; }
          .pe-mobile-actions > button { width: 100%; justify-content: center; }
          .pe-mobile-actions__primary { width: 100%; }
          .pe-mobile-actions__primary > button { flex: 1; justify-content: center; min-width: 0; }
        }

        * { box-sizing: border-box; }
      `}</style>

      <div style={s.page}>

        {/* ══════════════ NAVBAR ══════════════════════════════════════════════ */}
        <PublicHeader onLogin={() => { setPendingCompId(null); setShowLoginModal(true); }} />
        {false && <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 56, padding: isMobile ? "0 14px" : "0 32px",
          backgroundColor: "rgba(7,10,15,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <button
            className="pe-back-btn"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: `1px solid ${C.border2}`,
              borderRadius: 8, padding: "5px 11px", cursor: "pointer",
              color: C.text2, fontFamily: FONT.body,
              fontSize: 12, fontWeight: 600, letterSpacing: "0.4px",
              transition: "color 0.15s, border-color 0.15s",
            }}
            onClick={() => navigate("/")}
          >
            <BackIcon />
            {!isMobile && <span style={{ fontSize: 12, fontWeight: 600, fontFamily: FONT.body, letterSpacing: "0.4px" }}>{t("common.back")}</span>}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/geo.png" alt="Logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            {!isMobile && (
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 14, letterSpacing: "3px", color: C.text, lineHeight: 1.1 }}>GEORGIAN TAEKWONDO</div>
                <div style={{ fontFamily: FONT.display, fontSize: 9, letterSpacing: "2.5px", color: C.text3, lineHeight: 1 }}>NATIONAL FEDERATION</div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <button
              className="pe-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s", padding: "4px 6px",
              }}
              onClick={() => navigate("/rankings")}
            >
              <RankingsIcon />
              {!isMobile && <span>{t("landing.rankings")}</span>}
            </button>
            <LanguageSwitcher />
            {!currentUser && (
              <button className="pe-nav-sign" style={{
                backgroundColor: C.red, color: C.onAccent,
                border: "none", borderRadius: 8, cursor: "pointer",
                fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
                padding: isMobile ? "7px 12px" : "7px 16px",
                letterSpacing: "0.3px", transition: "background 0.15s",
              }} onClick={() => { setPendingCompId(null); setShowLoginModal(true); }}>
                {t("login.button")} →
              </button>
            )}
            {currentUser && userRole === "coach" && (
              <button className="pe-nav-sign" style={{
                backgroundColor: C.red, color: C.onAccent,
                border: "none", borderRadius: 8, cursor: "pointer",
                fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
                padding: isMobile ? "7px 12px" : "7px 16px",
                letterSpacing: "0.3px", transition: "background 0.15s",
              }} onClick={() => navigate("/dashboard")}>
                {isMobile ? "→" : t("nav.competitions")}
              </button>
            )}
            {currentUser && userRole === "admin" && (
              <button className="pe-nav-sign" style={{
                backgroundColor: C.red, color: C.onAccent,
                border: "none", borderRadius: 8, cursor: "pointer",
                fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
                padding: isMobile ? "7px 12px" : "7px 16px",
                letterSpacing: "0.3px", transition: "background 0.15s",
              }} onClick={() => navigate("/admin")}>Admin</button>
            )}
          </div>
        </header>}

        {/* Closed-competition banner — shown when the comp the coach was
            trying to register for closed between modal-open and login. */}
        {closedRegBanner && (
          <div
            role="alert"
            style={{
              maxWidth: 1280,
              margin: "12px auto 0",
              padding: isMobile ? "10px 14px" : "12px 18px",
              backgroundColor: "rgba(218,41,28,0.12)",
              border: "1px solid rgba(218,41,28,0.45)",
              borderRadius: 10,
              color: C.danger,
              fontFamily: FONT.body,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              ⚠ <strong>{closedRegBanner}</strong> is no longer open for
              registration. / <strong>{closedRegBanner}</strong> აღარ არის
              ღია რეგისტრაციისთვის.
            </span>
            <button
              onClick={() => setClosedRegBanner(null)}
              style={{
                background: C.surface2,
                color: C.text,
                border: "1px solid var(--color-border-strong)",
                borderRadius: 6,
                padding: "5px 10px",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FONT.body,
              }}
            >
              Dismiss / დახურვა
            </button>
          </div>
        )}

        {/* ══════════════ HERO ════════════════════════════════════════════════ */}
        <div className={heroLoaded ? "pe-loaded" : ""} style={s.hero}>
          {/* Background layers */}
          <div style={s.heroBgWrap}>
            <div style={{
              ...s.heroBg,
              backgroundImage: darkTheme ? s.heroBg.backgroundImage : "none",
              background: darkTheme ? undefined : "radial-gradient(circle at 82% 22%, var(--color-accent-soft), transparent 30%), linear-gradient(145deg, var(--color-surface), var(--color-surface-2))",
              animation: heroLoaded ? "heroBgZoom 1.8s cubic-bezier(.22,1,.36,1) forwards" : "none",
            }} />
            {darkTheme && <><div style={s.heroOverlay1} /><div style={s.heroOverlay2} /><div style={s.heroOverlay3} /></>}
          </div>

          {/* Hero content */}
          <div style={{
            ...s.heroContent,
            padding: isMobile ? "52px 20px 60px" : isTablet ? "60px 48px 72px" : "68px 80px 80px",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }}>
            <div className="pe-anim-0" style={s.heroEyebrow}>
              GEORGIAN TAEKWONDO FEDERATION
            </div>
            <h1 className="pe-anim-1" style={{
              ...s.heroTitle,
              color: darkTheme ? C.heroText : C.text,
              fontSize: isMobile ? "clamp(44px,13vw,64px)" : isTablet ? "clamp(58px,8vw,80px)" : "clamp(68px,7vw,96px)",
            }}>
              {t("landing.events")}
            </h1>
            <p className="pe-anim-2" style={{
              ...s.heroSub,
              color: darkTheme ? C.heroText2 : C.text2,
              maxWidth: isMobile ? "100%" : "480px",
            }}>
              {t("dashboard.subheading")}
            </p>

            {/* Stats row */}
            <div className="pe-anim-3" style={{
              display: "flex", gap: isMobile ? 10 : 16,
              justifyContent: isMobile ? "center" : "flex-start",
              flexWrap: "wrap",
              marginTop: 4,
            }}>
              <div className="pe-stat-pill" style={s.statPill}>
                <span style={s.statPillNum}>{openComps.length}</span>
                <span style={s.statPillLabel}>{t("dashboard.tab.open")}</span>
              </div>
              <div style={s.statDivider} />
              <div className="pe-stat-pill" style={s.statPill}>
                <span style={s.statPillNum}>{competitions.length}</span>
                <span style={s.statPillLabel}>Total</span>
              </div>
              <div style={s.statDivider} />
              <div className="pe-stat-pill" style={s.statPill}>
                <span style={s.statPillNum}>{pastComps.length}</span>
                <span style={s.statPillLabel}>{t("dashboard.tab.past")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════ TABS ════════════════════════════════════════════════ */}
        <div style={s.tabsWrap}>
          <div style={{
            ...s.tabsInner,
            padding: isMobile ? "0 14px" : "0 32px",
            justifyContent: isMobile ? "space-around" : "flex-start",
          }}>
            {(["open", "past"] as const).map((tk) => {
              const count = (tk === "open" ? openComps : pastComps).length;
              const active = tab === tk;
              return (
                <button
                  key={tk}
                  className="pe-tab-btn"
                  style={{
                    ...s.tabBtn,
                    ...(active ? s.tabBtnActive : s.tabBtnInactive),
                  }}
                  onClick={() => setTab(tk)}
                >
                  {tk === "open" ? t("dashboard.tab.open") : t("dashboard.tab.past")}
                  {count > 0 && (
                    <span style={{
                      ...s.tabBadge,
                      ...(active ? s.tabBadgeActive : {}),
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Rankings link pill */}
            <button
              className="pe-rankings-link"
              style={{
                ...s.rankingsLinkBtn,
                marginLeft: isMobile ? 0 : "auto",
                display: isMobile ? "none" : "inline-flex",
              }}
              onClick={() => navigate("/rankings")}
            >
              <RankingsIcon />
              <span>{t("landing.rankings")}</span>
            </button>
          </div>
        </div>

        {/* ══════════════ EVENTS GRID ═════════════════════════════════════════ */}
        <section style={{
          ...s.listSection,
          padding: isMobile ? "28px 14px 64px" : isTablet ? "32px 24px 64px" : "36px 40px 72px",
        }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>

            {/* ── Search bar ── */}
            {!loadingComps && displayed.length > 0 && (
              <div style={{
                position: "relative",
                marginBottom: isMobile ? 16 : 20,
              }}>
                <span style={{
                  position: "absolute",
                  left: isMobile ? 10 : 13,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: C.text3,
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none",
                }}>
                  <SearchIcon />
                </span>
                <input
                  className="pe-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("admin.allRegistrations.search")}
                  style={{
                    width: "100%",
                    padding: isMobile ? "9px 10px 9px 32px" : "11px 14px 11px 38px",
                    backgroundColor: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    color: C.text,
                    fontSize: isMobile ? 13 : 14,
                    fontFamily: FONT.body,
                    fontWeight: 500,
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxSizing: "border-box",
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: C.text3,
                      display: "flex",
                      alignItems: "center",
                      padding: 4,
                      borderRadius: 4,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Grid */}
            <div className="pe-events-grid" style={{
              display: "grid",
              gap: isMobile ? 14 : 18,
            }}>

              {/* Loading skeleton */}
              {loadingComps && (
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{ ...s.skeletonCard, minHeight: isMobile ? 180 : 280 }}>
                      <div style={s.skeletonHeader} />
                      <div style={{ padding: "16px 14px" }}>
                        <div style={{ ...s.skeletonLine, width: "70%", marginBottom: 10 }} />
                        <div style={{ ...s.skeletonLine, width: "50%", marginBottom: 8 }} />
                        <div style={{ ...s.skeletonLine, width: "90%" }} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty state — no competitions in this tab */}
              {!loadingComps && displayed.length === 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={s.emptyWrap}>
                    <div style={s.emptyIcon}><TrophyIcon /></div>
                    <div style={s.emptyTitle}>
                      {tab === "open" ? t("dashboard.empty.title") : t("dashboard.past.empty.title")}
                    </div>
                    <div style={s.emptyText}>
                      {tab === "open" ? t("dashboard.empty.sub") : t("dashboard.past.empty.sub")}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state — search returned nothing */}
              {!loadingComps && displayed.length > 0 && filteredDisplayed.length === 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={s.emptyWrap}>
                    <div style={s.emptyIcon}><SearchIcon /></div>
                    <div style={s.emptyTitle}>{t("events.noSearchResults")}</div>
                    <div style={s.emptyText}>{t("events.tryDifferentSearch")}</div>
                  </div>
                </div>
              )}

              {/* Competition cards */}
              {!loadingComps && displayedComps.map((comp) => {
                const open = isOpen(comp);
                const expanded = expandedIds.has(comp.id);
                const price = currentPrice(comp);

                // ── Mobile: horizontal compact card ──────────────────────
                if (isMobile) {
                  return (
                    <div key={comp.id} style={s.cardMobile}>
                      {/* Top accent bar */}
                      <div style={{
                        height: 2,
                        background: open
                          ? "linear-gradient(90deg, #da291c, rgba(218,41,28,0.4))"
                          : "var(--color-border)",
                      }} />

                      <div style={{ display: "flex", flexDirection: "row", padding: "12px 10px", gap: 10 }}>
                        {/* Left image */}
                        {comp.imageUrl ? (
                          <div style={{
                            width: 80, height: 80, minWidth: 80, borderRadius: 8,
                            backgroundImage: `url(${comp.imageUrl})`,
                            backgroundSize: "cover", backgroundPosition: "center",
                            flexShrink: 0,
                          }} />
                        ) : (
                          <div style={{
                            width: 80, height: 80, minWidth: 80, borderRadius: 8,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            backgroundColor: open ? "rgba(218,41,28,0.08)" : "var(--color-surface-2)",
                            border: `1px solid ${C.border}`,
                            flexShrink: 0,
                          }}>
                            <div style={{ color: open ? "rgba(218,41,28,0.4)" : "var(--color-border)", transform: "scale(0.8)" }}>
                              <KarateIcon />
                            </div>
                          </div>
                        )}

                        {/* Right content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ ...s.statusBadgeMobile, ...(open ? s.statusOpen : s.statusClosed) }}>
                              {open ? t("admin.competitions.status.open") : t("admin.competitions.status.closed")}
                            </span>
                          </div>
                          <div style={s.compNameMobile}>{comp.name}</div>

                          {/* Type pills */}
                          {comp.types && comp.types.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
                              {comp.types.map((tp) => (
                                <span key={tp} style={s.typePillMobile}>{tp}</span>
                              ))}
                            </div>
                          )}

                          {/* Meta lines */}
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ color: C.text3, flexShrink: 0 }}><CalendarIcon /></span>
                              <span style={{ fontSize: 11, color: C.text2, fontWeight: 500, fontFamily: FONT.body }}>
                                {fmt(comp.startDate)}{comp.endDate !== comp.startDate ? ` – ${fmt(comp.endDate)}` : ""}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ color: C.text3, flexShrink: 0 }}><ClockIcon /></span>
                              <span style={{ fontSize: 11, color: open ? "#da291c" : "var(--color-text-muted)", fontWeight: open ? 700 : 500, fontFamily: FONT.body }}>
                                {fmt(comp.registrationDeadline)}
                              </span>
                            </div>
                            {comp.weightChangeDeadline && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <span style={{ color: C.text3, flexShrink: 0 }}><ScaleIcon /></span>
                                <span style={{ fontSize: 11, color: C.text2, fontWeight: 500, fontFamily: FONT.body }}>
                                  {t("events.weightDeadline")}: {fmt(comp.weightChangeDeadline)}
                                </span>
                              </div>
                            )}
                            {comp.documentUrl && (
                              <a
                                href={comp.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}
                              >
                                <span style={{ flexShrink: 0 }}>📄</span>
                                <span style={{ fontSize: 11, color: "#60a5fa", fontWeight: 700, fontFamily: FONT.body, textDecoration: "underline" }}>
                                  {t("events.officialDoc")}
                                </span>
                              </a>
                            )}
                            {!comp.imageUrl && price > 0 && open && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <span style={{ color: C.text3, flexShrink: 0 }}><PriceIcon /></span>
                                <span style={{ fontSize: 11, color: C.red, fontWeight: 700, fontFamily: FONT.body }}>{price} GEL</span>
                              </div>
                            )}
                          </div>

                          {/* Toggle + register */}
                          <div className="pe-mobile-actions" style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                            <button
                              className="pe-details-btn"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: "none", border: `1px solid ${C.border}`,
                                borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 600,
                                color: C.text3, cursor: "pointer",
                                fontFamily: FONT.body,
                              }}
                              onClick={() => toggleExpand(comp.id)}
                            >
                              <span>{expanded ? t("admin.competitions.hideDetails") : t("admin.competitions.showDetails")}</span>
                              <ChevronIcon open={expanded} />
                            </button>
                            <div className="pe-mobile-actions__primary" style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
                              {open && (
                                <button
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    backgroundColor: C.red, color: C.onAccent, border: "none",
                                    borderRadius: 6, padding: "5px 10px", fontSize: 10,
                                    fontWeight: 700, cursor: "pointer", fontFamily: FONT.body,
                                    letterSpacing: "0.3px",
                                  }}
                                  onClick={() => handleRegisterClick(comp.id)}
                                >
                                  <RegisterIcon />
                                  {t("dashboard.registerBtn")}
                                </button>
                              )}
                              <button
                                aria-label={t("publicDraws.drawTab")}
                                title={t("publicDraws.drawTab")}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: C.surface2, color: C.text,
                                  border: `1px solid ${C.border2}`,
                                  borderRadius: 6, padding: "5px 9px", fontSize: 10,
                                  fontWeight: 700, cursor: "pointer", fontFamily: FONT.body,
                                }}
                                onClick={() => navigate(`/competition/${comp.id}/draws`)}
                              >
                                <span aria-hidden="true">▦</span>
                                {t("publicDraws.drawTab")}
                              </button>
                              <button
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: C.surface2,
                                  color: C.text,
                                  border: `1px solid ${C.border2}`,
                                  borderRadius: 6, padding: "5px 10px", fontSize: 10,
                                  fontWeight: 700, cursor: "pointer", fontFamily: FONT.body,
                                  letterSpacing: "0.3px",
                                }}
                                onClick={() => navigate(`/competition/${comp.id}/registrations`)}
                              >
                                {t("publicRegs.viewAthletes")} →
                              </button>
                            </div>
                          </div>

                          {/* Expanded details (mobile) */}
                          {expanded && (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              {(comp.earlyPrice !== undefined || comp.midPrice !== undefined || comp.latePrice !== undefined) && (
                                <div>
                                  <div style={{ fontSize: 8, fontWeight: 800, color: C.text3, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6, fontFamily: FONT.body }}>
                                    {t("dashboard.pricing.title")}
                                  </div>
                                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                    {comp.earlyPrice !== undefined && comp.earlyDeadline && (
                                      <div style={{ flex: 1, minWidth: 60, padding: "6px 8px", backgroundColor: C.surface2, borderRadius: 7, border: `1px solid ${C.border}`, ...(currentPrice(comp) === comp.earlyPrice && open ? { backgroundColor: "rgba(218,41,28,0.1)", border: "1px solid rgba(218,41,28,0.28)" } : {}) }}>
                                        <div style={{ fontSize: 7, fontWeight: 700, color: C.text3, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2, fontFamily: FONT.body }}>{t("dashboard.pricing.early")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: C.red, lineHeight: 1, marginBottom: 2, fontFamily: FONT.display }}>{comp.earlyPrice} GEL</div>
                                        <div style={{ fontSize: 8, color: C.text3, fontFamily: FONT.body }}>{t("admin.competitions.labels.until")} {fmt(comp.earlyDeadline)}</div>
                                      </div>
                                    )}
                                    {comp.midPrice !== undefined && comp.midDeadline && (
                                      <div style={{ flex: 1, minWidth: 60, padding: "6px 8px", backgroundColor: C.surface2, borderRadius: 7, border: `1px solid ${C.border}`, ...(currentPrice(comp) === comp.midPrice && open ? { backgroundColor: "rgba(218,41,28,0.1)", border: "1px solid rgba(218,41,28,0.28)" } : {}) }}>
                                        <div style={{ fontSize: 7, fontWeight: 700, color: C.text3, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2, fontFamily: FONT.body }}>{t("dashboard.pricing.mid")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: C.red, lineHeight: 1, marginBottom: 2, fontFamily: FONT.display }}>{comp.midPrice} GEL</div>
                                        <div style={{ fontSize: 8, color: C.text3, fontFamily: FONT.body }}>{t("admin.competitions.labels.until")} {fmt(comp.midDeadline)}</div>
                                      </div>
                                    )}
                                    {comp.latePrice !== undefined && (
                                      <div style={{ flex: 1, minWidth: 60, padding: "6px 8px", backgroundColor: C.surface2, borderRadius: 7, border: `1px solid ${C.border}`, ...(currentPrice(comp) === comp.latePrice && open ? { backgroundColor: "rgba(218,41,28,0.1)", border: "1px solid rgba(218,41,28,0.28)" } : {}) }}>
                                        <div style={{ fontSize: 7, fontWeight: 700, color: C.text3, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2, fontFamily: FONT.body }}>{t("dashboard.pricing.late")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: C.red, lineHeight: 1, marginBottom: 2, fontFamily: FONT.display }}>{comp.latePrice} GEL</div>
                                        <div style={{ fontSize: 8, color: C.text3, fontFamily: FONT.body }}>—</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                {comp.maleCategories && comp.maleCategories.length > 0 && (
                                  <div style={{ flex: 1, minWidth: 80 }}>
                                    <div style={{ fontSize: 8, fontWeight: 700, color: "rgba(96,165,250,0.85)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5, display: "flex", alignItems: "center", fontFamily: FONT.body }}>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}><path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z" /></svg>
                                      {t("admin.competitions.labels.male")}
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                      {comp.maleCategories.map((wt) => (
                                        <span key={wt} style={{ fontSize: 9, padding: "2px 6px", backgroundColor: "rgba(59,130,246,0.1)", color: "rgba(96,165,250,0.85)", borderRadius: 20, fontWeight: 600, border: "1px solid rgba(59,130,246,0.2)", fontFamily: FONT.body }}>{wt}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {comp.femaleCategories && comp.femaleCategories.length > 0 && (
                                  <div style={{ flex: 1, minWidth: 80 }}>
                                    <div style={{ fontSize: 8, fontWeight: 700, color: "rgba(236,72,153,0.85)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5, display: "flex", alignItems: "center", fontFamily: FONT.body }}>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}><path d="M11 2a5 5 0 1 0 0 10A5 5 0 0 0 11 2zm1 10.93V15h2v2h-2v2h-2v-2H8v-2h2v-2.07A7.001 7.001 0 0 1 4 9V7h2v2a5 5 0 0 0 10 0V7h2v2a7.001 7.001 0 0 1-6 6.93z" /></svg>
                                      {t("admin.competitions.labels.female")}
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                      {comp.femaleCategories.map((wt) => (
                                        <span key={wt} style={{ fontSize: 9, padding: "2px 6px", backgroundColor: "rgba(236,72,153,0.1)", color: "rgba(236,72,153,0.85)", borderRadius: 20, fontWeight: 600, border: "1px solid rgba(236,72,153,0.2)", fontFamily: FONT.body }}>{wt}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // ── Desktop: redesigned horizontal card (compact) ─────────────
                return (
                  <div key={comp.id} className="pe-card" style={s.deskCard}>
                    {/* Left side – image / placeholder */}
                    {comp.imageUrl ? (
                                         <div style={{ ...s.deskCardImage, backgroundImage: `url(${comp.imageUrl})` }}>
                      <div style={{ ...s.statusBadge, ...(open ? s.statusOpen : s.statusClosed), position: "absolute", top: 12, left: 12 }}>
                        {open ? t("admin.competitions.status.open") : t("admin.competitions.status.closed")}
                      </div>
                      {price > 0 && open && (
                        <div style={s.deskImagePriceBadge}>{price} GEL</div>
                      )}
                    </div>
                    ) : (
                      <div style={{ ...s.deskCardPlaceholder, ...(open ? s.placeholderOpen : s.placeholderClosed) }}>
                        <div style={{ color: open ? "rgba(218,41,28,0.4)" : "var(--color-border)" }}>
                          <KarateIcon />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                          <div style={{ ...s.statusBadge, ...(open ? s.statusOpen : s.statusClosed) }}>
                            {open ? t("admin.competitions.status.open") : t("admin.competitions.status.closed")}
                          </div>
                          {price > 0 && open && (
                            <div style={s.deskPlaceholderPriceBadge}>{price} GEL</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Right side – all content */}
                    <div style={s.deskCardBody}>
                      {/* Name */}
                      <div style={s.deskCompName}>{comp.name}</div>

                      {/* Type pills */}
                      {comp.types && comp.types.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
                          {comp.types.map((tp) => (
                            <span key={tp} style={s.deskTypePill}>{tp}</span>
                          ))}
                        </div>
                      )}

                      {/* Meta block (date/deadline) */}
                      <div style={s.deskMetaBlock}>
                        <div style={s.deskMetaLine}>
                          <span style={s.deskMetaIcon}><CalendarIcon /></span>
                          <span style={s.deskMetaKey}>{t("dashboard.competition.dates")}</span>
                          <span style={s.deskMetaVal}>
                            {fmt(comp.startDate)}{comp.endDate !== comp.startDate ? ` – ${fmt(comp.endDate)}` : ""}
                          </span>
                        </div>
                        <div style={s.deskMetaLine}>
                          <span style={s.deskMetaIcon}><ClockIcon /></span>
                          <span style={s.deskMetaKey}>{t("dashboard.competition.deadline")}</span>
                            <span className="pe-card__meta-value" style={{ ...s.deskMetaVal, color: open ? C.red : C.text2, fontWeight: open ? 700 : 500 }}>
                            {fmt(comp.registrationDeadline)}
                          </span>
                        </div>
                        {comp.weightChangeDeadline && (
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}><ScaleIcon /></span>
                            <span style={s.deskMetaKey}>{t("events.weightDeadline")}</span>
                            <span className="pe-card__meta-value" style={s.deskMetaVal}>{fmt(comp.weightChangeDeadline)}</span>
                          </div>
                        )}
                        {comp.documentUrl && (
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}>📄</span>
                            <span style={s.deskMetaKey}>{t("events.officialDoc")}</span>
                            <a
                              href={comp.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="pe-card__meta-value"
                              style={{ ...s.deskMetaVal, color: C.blue, fontWeight: 700, textDecoration: "underline" }}
                            >
                              {t("events.officialDocOpen")}
                            </a>
                          </div>
                        )}
                        {!comp.imageUrl && price > 0 && open && (
                          <div style={s.deskMetaLine}>
                            <span style={s.deskMetaIcon}><PriceIcon /></span>
                            <span style={s.deskMetaKey}>{t("dashboard.pricing.current")}</span>
                            <span style={{ ...s.deskMetaVal, color: C.red, fontWeight: 700 }}>{price} GEL</span>
                          </div>
                        )}
                      </div>

                      {/* Details toggle */}
                      <button
                        className="pe-details-btn"
                        style={s.deskDetailsToggle}
                        onClick={() => toggleExpand(comp.id)}
                      >
                        <span>{expanded ? t("admin.competitions.hideDetails") : t("admin.competitions.showDetails")}</span>
                        <ChevronIcon open={expanded} />
                      </button>

                      {/* Expanded details (pricing, categories) */}
                      {expanded && (
                        <div style={s.deskDetailsPanel}>
                          {(comp.earlyPrice !== undefined || comp.midPrice !== undefined || comp.latePrice !== undefined) && (
                            <div>
                              <div style={s.deskDetailLabel}>{t("dashboard.pricing.title")}</div>
                              <div style={s.deskPricingRow}>
                                {comp.earlyPrice !== undefined && comp.earlyDeadline && (
                                  <div style={{ ...s.deskPriceCell, ...(currentPrice(comp) === comp.earlyPrice && open ? s.deskPriceCellActive : {}) }}>
                                    <div style={s.deskPriceCellLabel}>{t("dashboard.pricing.early")}</div>
                                    <div style={s.deskPriceCellAmount}>{comp.earlyPrice} GEL</div>
                                    <div style={s.deskPriceCellUntil}>{t("admin.competitions.labels.until")} {fmt(comp.earlyDeadline)}</div>
                                  </div>
                                )}
                                {comp.midPrice !== undefined && comp.midDeadline && (
                                  <div style={{ ...s.deskPriceCell, ...(currentPrice(comp) === comp.midPrice && open ? s.deskPriceCellActive : {}) }}>
                                    <div style={s.deskPriceCellLabel}>{t("dashboard.pricing.mid")}</div>
                                    <div style={s.deskPriceCellAmount}>{comp.midPrice} GEL</div>
                                    <div style={s.deskPriceCellUntil}>{t("admin.competitions.labels.until")} {fmt(comp.midDeadline)}</div>
                                  </div>
                                )}
                                {comp.latePrice !== undefined && (
                                  <div style={{ ...s.deskPriceCell, ...(currentPrice(comp) === comp.latePrice && open ? s.deskPriceCellActive : {}) }}>
                                    <div style={s.deskPriceCellLabel}>{t("dashboard.pricing.late")}</div>
                                    <div style={s.deskPriceCellAmount}>{comp.latePrice} GEL</div>
                                    <div style={s.deskPriceCellUntil}>—</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {comp.maleCategories && comp.maleCategories.length > 0 && (
                              <div style={{ flex: 1, minWidth: 110 }}>
                                <div style={s.deskWeightColLabel}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}><path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z" /></svg>
                                  {t("admin.competitions.labels.male")}
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                  {comp.maleCategories.map((wt) => (
                                    <span key={wt} style={s.deskWeightPillMale}>{wt}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {comp.femaleCategories && comp.femaleCategories.length > 0 && (
                              <div style={{ flex: 1, minWidth: 110 }}>
                                <div style={{ ...s.deskWeightColLabel, color: "rgba(236,72,153,0.85)" }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}><path d="M11 2a5 5 0 1 0 0 10A5 5 0 0 0 11 2zm1 10.93V15h2v2h-2v2h-2v-2H8v-2h2v-2.07A7.001 7.001 0 0 1 4 9V7h2v2a5 5 0 0 0 10 0V7h2v2a7.001 7.001 0 0 1-6 6.93z" /></svg>
                                  {t("admin.competitions.labels.female")}
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                  {comp.femaleCategories.map((wt) => (
                                    <span key={wt} style={s.deskWeightPillFemale}>{wt}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Register + view-athletes buttons */}
                      <div className="pe-card__actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-start" }}>
                        {open && (
                          <button
                            className="pe-register-btn"
                            style={s.deskRegisterBtn}
                            onClick={() => handleRegisterClick(comp.id)}
                          >
                            <RegisterIcon />
                            {t("dashboard.registerBtn")}
                          </button>
                        )}
                        <button
                          aria-label={t("publicDraws.drawTab")}
                          title={t("publicDraws.drawTab")}
                          onClick={() => navigate(`/competition/${comp.id}/draws`)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: C.surface2, color: C.text,
                            border: `1px solid ${C.border2}`,
                            borderRadius: 8, padding: "8px 12px",
                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                            fontFamily: FONT.body, flex: "0 1 auto", justifyContent: "center",
                          }}
                        >
                          <span aria-hidden="true">▦</span>
                          {t("publicDraws.drawTab")}
                        </button>
                        <button
                          onClick={() => navigate(`/competition/${comp.id}/registrations`)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: C.surface2,
                            color: C.text,
                            border: `1px solid ${C.border2}`,
                            borderRadius: 8, padding: "8px 14px",
                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                            fontFamily: FONT.body, letterSpacing: "0.3px", flex: "0 1 auto", justifyContent: "center",
                          }}
                        >
                          {t("publicRegs.viewAthletes")} →
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Past tab pagination ── */}
            {!loadingComps && tab === "past" && pastTotalPages > 1 && (
              <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 6,
                marginTop: 32,
                flexWrap: "wrap",
              }}>
                {Array.from({ length: pastTotalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => { setPastPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    style={{
                      minWidth: 36,
                      height: 36,
                      padding: "0 10px",
                      borderRadius: 8,
                      border: page === pastPage
                        ? "1px solid #da291c"
                        : "1px solid var(--color-border-strong)",
                      backgroundColor: page === pastPage
                        ? "#da291c"
                        : "var(--color-border)",
                      color: page === pastPage ? "#fff" : "var(--color-text-muted)",
                      fontSize: 13,
                      fontWeight: page === pastPage ? 700 : 500,
                      fontFamily: FONT.body,
                      cursor: "pointer",
                      transition: "background 0.15s, color 0.15s, border-color 0.15s",
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ══════════════ FOOTER STRIP ════════════════════════════════════════ */}
        <footer style={{
          ...s.footer,
          flexDirection: isMobile ? "column" : "row",
          textAlign: isMobile ? "center" : "left",
          gap: isMobile ? 14 : 0,
          padding: isMobile ? "24px 20px" : "20px 40px",
        }}>
          <div style={s.footerLeft}>
            <img src="/geo.png" alt="Logo" style={{ height: 22, opacity: 0.4, objectFit: "contain" }} />
            <span style={s.footerText}>© {new Date().getFullYear()} Georgian Taekwondo National Federation</span>
          </div>
          <div style={s.footerRight}>
            <button style={s.footerLink} onClick={() => navigate("/")}>Home</button>
            <span style={s.footerDivider}>·</span>
            <button style={s.footerLink} onClick={() => navigate("/rankings")}>{t("landing.rankings")}</button>
            <span style={s.footerDivider}>·</span>
            <button style={s.footerLink} onClick={() => navigate("/privacy")}>{t("landing.privacy")}</button>
          </div>
        </footer>
      </div>

      {/* ══════════════ LOGIN MODAL ══════════════════════════════════════════════ */}
      {showLoginModal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalAccentBar} />
            <div style={s.modalHeader}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <img src="/geo.png" alt="Logo" style={{ height: 32, objectFit: "contain" }} />
                  <div style={s.modalTitle}>{t("landing.loginTitle")}</div>
                </div>
                <div style={s.modalSub}>{t("landing.loginSub")}</div>
              </div>
              <button style={s.closeBtn} onClick={closeModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {loginError && (
              <div style={s.errorBox}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.red, display: "inline-block", flexShrink: 0 }} />
                {loginError}
              </div>
            )}

            {resetMessage && (
              <div style={{
                margin: "0 22px 14px",
                backgroundColor: "rgba(34,197,94,0.10)",
                border: "1px solid rgba(34,197,94,0.35)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                lineHeight: 1.5,
                color: "#4ade80",
              }}>
                {resetMessage}
              </div>
            )}

            {authView === "login" ? (
              <form onSubmit={handleLogin} style={{ padding: "0 22px" }}>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>{t("login.email")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "email" ? s.inputFocus : {}) }}
                    type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField("")}
                    placeholder="you@example.com"
                    required autoComplete="email"
                  />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>{t("login.password")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "password" ? s.inputFocus : {}) }}
                    type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField("")}
                    placeholder="••••••••"
                    required autoComplete="current-password"
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => switchAuthView("forgot")}
                    style={{ background: "none", border: "none", color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: FONT.body }}
                  >
                    {t("login.forgotLink")}
                  </button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <button style={s.submitBtn} type="submit" disabled={loginLoading}>
                    {loginLoading ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <span style={s.spinner} /> {t("login.loading")}
                      </span>
                    ) : t("login.button")}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetRequest} style={{ padding: "0 22px" }}>
                <div style={{ fontSize: 13, lineHeight: 1.5, color: C.text2, marginBottom: 14 }}>
                  {t("login.reset.subtitle")}
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>{t("login.email")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "resetEmail" ? s.inputFocus : {}) }}
                    type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField("resetEmail")}
                    onBlur={() => setFocusedField("")}
                    placeholder="you@example.com"
                    required autoComplete="email"
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <button style={s.submitBtn} type="submit" disabled={resetLoading}>
                    {resetLoading ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <span style={s.spinner} /> {t("login.reset.sending")}
                      </span>
                    ) : t("login.reset.button")}
                  </button>
                </div>
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <button
                    type="button"
                    onClick={() => switchAuthView("login")}
                    style={{ background: "none", border: "none", color: C.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: FONT.body }}
                  >
                    {t("login.reset.back")}
                  </button>
                </div>
              </form>
            )}
            <div style={s.modalFooter}>{t("login.footer")}</div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    fontFamily: FONT.body,
  },

  // ── Navbar
  navbar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "rgba(7,10,15,0.92)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: `1px solid ${C.border}`,
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    color: C.text2,
    cursor: "pointer",
    padding: "6px 10px",
    transition: "color 0.15s, border-color 0.15s",
    fontFamily: FONT.body,
    flexShrink: 0,
  },
  navCenter: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  navLogo: {
    height: 28,
    objectFit: "contain",
    opacity: 1,
  },
  navBrandTop: {
    fontSize: 11,
    fontWeight: 800,
    color: C.text,
    letterSpacing: "1.5px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navBrandBot: {
    fontSize: 9,
    fontWeight: 600,
    color: C.text2,
    letterSpacing: "1px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navTextLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    color: C.text2,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    padding: "4px 8px",
    transition: "color 0.15s",
  },
  signInBtn: {
    padding: "7px 14px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
    transition: "background 0.15s",
  },

  // ── Hero
  hero: {
    position: "relative",
    overflow: "hidden",
    minHeight: 280,
  },
  heroBgWrap: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    backgroundImage: "url('/hero-bg.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center 30%",
    willChange: "transform",
  },
  heroOverlay1: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(7,10,15,0.92) 0%, rgba(7,10,15,0.6) 100%)",
  },
  heroOverlay2: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(0deg, var(--color-bg) 0%, transparent 60%)",
  },
  heroOverlay3: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(ellipse at 15% 50%, rgba(218,41,28,0.12) 0%, transparent 60%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  heroEyebrow: {
    fontSize: 10,
    fontWeight: 800,
    color: C.red,
    letterSpacing: "3px",
    fontFamily: FONT.body,
  },
  heroTitle: {
    fontFamily: FONT.display,
    color: C.heroText,
    lineHeight: 0.95,
    letterSpacing: "1px",
    margin: 0,
  },
  heroSub: {
    fontSize: 14,
    color: C.heroText2,
    lineHeight: 1.6,
    fontFamily: FONT.body,
    margin: 0,
  },
  statPill: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 14px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 40,
    cursor: "default",
    transition: "background 0.15s",
  },
  statPillNum: {
    fontSize: 20,
    fontWeight: 800,
    color: C.text,
    fontFamily: FONT.display,
    letterSpacing: "0.5px",
    lineHeight: 1,
  },
  statPillLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    fontFamily: FONT.body,
  },
  statDivider: {
    width: 1,
    height: 28,
    background: "var(--color-border-strong)",
    alignSelf: "center",
  },

  // ── Tabs
  tabsWrap: {
    backgroundColor: C.surface2,
    borderBottom: `1px solid ${C.border}`,
    position: "sticky",
    top: 58,
    zIndex: 90,
  },
  tabsInner: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
  },
  tabBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 18px",
    border: "none",
    background: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
    borderBottom: "2px solid transparent",
    transition: "color 0.15s, border-color 0.15s",
    marginBottom: -1,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  tabBtnActive: {
    color: C.text,
    borderBottom: `2px solid ${C.red}`,
  },
  tabBtnInactive: {
    color: C.text3,
  },
  tabBadge: {
    backgroundColor: C.surface2,
    color: C.text3,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    transition: "background 0.15s, color 0.15s",
  },
  tabBadgeActive: {
    backgroundColor: C.red,
    color: C.onAccent,
  },
  rankingsLinkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text2,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    transition: "background 0.15s",
  },

  // ── List section
  listSection: {
    backgroundColor: C.bg,
    minHeight: 320,
  },

  // ── Skeleton
  skeletonCard: {
    backgroundColor: C.surface,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid var(--color-border)",
  },
  skeletonHeader: {
    height: 72,
    background: C.surface2,
  },
  skeletonLine: {
    height: 10,
    borderRadius: 6,
    background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-border-strong) 50%, var(--color-border) 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
    marginBottom: 8,
  },

  // ── Empty state
  emptyWrap: {
    textAlign: "center",
    padding: "72px 20px",
  },
  emptyIcon: {
    color: "var(--color-border)",
    marginBottom: 14,
    display: "flex",
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text2,
    marginBottom: 6,
    fontFamily: FONT.body,
  },
  emptyText: {
    fontSize: 13,
    color: C.text3,
    fontFamily: FONT.body,
  },

    cardMobile: {
    backgroundColor: C.surface,
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    marginBottom: 12,
  },
  card: {
    backgroundColor: C.surface,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
    border: `1px solid ${C.border}`,
  },
  cardImage: {
    height: 140,
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
  },
  // Mobile variant: shorter image
  cardImageMobile: {
    height: 80,
  },
  cardPlaceholder: {
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
  },
  // Mobile variant: shorter placeholder
  cardPlaceholderMobile: {
    height: 48,
    padding: "0 12px",
  },
  placeholderOpen: {
    background: "linear-gradient(135deg, rgba(218,41,28,0.08) 0%, rgba(218,41,28,0.03) 100%)",
    borderBottom: "1px solid rgba(218,41,28,0.1)",
  },
  placeholderClosed: {
    background: C.surface2,
    borderBottom: `1px solid ${C.border}`,
  },
  statusBadge: {
    fontSize: 9,
    fontWeight: 800,
    padding: "3px 9px",
    borderRadius: 20,
    letterSpacing: "0.7px",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  statusBadgeMobile: {
    fontSize: 7,
    fontWeight: 800,
    padding: "1px 5px",
    borderRadius: 12,
    letterSpacing: "0.3px",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  statusOpen: {
    backgroundColor: C.successDim,
    color: C.success,
    border: `1px solid ${C.successBorder}`,
  },
  statusClosed: {
    backgroundColor: C.surface2,
    color: C.text3,
    border: `1px solid ${C.border}`,
  },
  imagePriceBadge: {
    position: "absolute",
    bottom: 10,
    left: 10,
    backgroundColor: "rgba(218,41,28,0.9)",
    backdropFilter: "blur(4px)",
    color: C.onAccent,
    borderRadius: 20,
    padding: "3px 10px",
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid var(--color-text-subtle)",
    fontFamily: FONT.body,
  },
  cardBody: {
    padding: "16px 16px 18px",
  },
  // Mobile variant: tighter card body
  cardBodyMobile: {
    padding: "10px 11px 12px",
  },
  compName: {
    fontFamily: FONT.display,
    fontSize: 19,
    letterSpacing: "0.5px",
    color: C.text,
    lineHeight: 1.15,
    marginBottom: 10,
  },
  // Mobile variant: smaller name
  compNameMobile: {
    fontFamily: FONT.display,
    fontSize: 15,
    letterSpacing: "0.4px",
    color: C.text,
    lineHeight: 1.15,
    marginBottom: 7,
  },
  typePill: {
    fontSize: 9,
    padding: "2px 9px",
    backgroundColor: "rgba(218,41,28,0.12)",
    color: C.red,
    borderRadius: 20,
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: "1px solid rgba(218,41,28,0.2)",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  // Mobile variant: same visual but tagged separately for future tweaks
  typePillMobile: {
    fontSize: 8,
    padding: "2px 7px",
    backgroundColor: "rgba(218,41,28,0.12)",
    color: C.red,
    borderRadius: 20,
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: "1px solid rgba(218,41,28,0.2)",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  metaBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    marginBottom: 12,
    padding: "10px 12px",
    backgroundColor: C.surface2,
    borderRadius: 9,
    border: `1px solid ${C.border}`,
  },
  // Mobile variant: tighter meta block
  metaBlockMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    marginBottom: 8,
    padding: "7px 9px",
    backgroundColor: C.surface2,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
  },
  metaLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  metaIcon: {
    color: C.text3,
    flexShrink: 0,
  },
  metaKey: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.6px",
    flexShrink: 0,
    minWidth: 62,
    fontFamily: FONT.body,
  },
  // Mobile variant: smaller label, narrower min-width
  metaKeyMobile: {
    fontSize: 9,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    flexShrink: 0,
    minWidth: 52,
    fontFamily: FONT.body,
  },
  metaVal: {
    fontSize: 12,
    color: C.text,
    fontWeight: 500,
    fontFamily: FONT.body,
  },
  // Mobile variant: smaller value text
  metaValMobile: {
    fontSize: 11,
    color: C.text,
    fontWeight: 500,
    fontFamily: FONT.body,
  },
  detailsToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: C.text3,
    cursor: "pointer",
    fontFamily: FONT.body,
    marginBottom: 12,
    transition: "border-color 0.15s, color 0.15s",
  },
  // Mobile variant: smaller toggle button
  detailsToggleMobile: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: "4px 9px",
    fontSize: 10,
    fontWeight: 600,
    color: C.text3,
    cursor: "pointer",
    fontFamily: FONT.body,
    marginBottom: 8,
    transition: "border-color 0.15s, color 0.15s",
  },
  detailsPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    marginBottom: 14,
  },
  detailLabel: {
    fontSize: 9,
    fontWeight: 800,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    marginBottom: 8,
    fontFamily: FONT.body,
  },
  pricingRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap" as const,
  },
  priceCell: {
    flex: 1,
    minWidth: 80,
    padding: "9px 11px",
    backgroundColor: C.surface2,
    borderRadius: 9,
    border: `1px solid ${C.border}`,
  },
  priceCellActive: {
    backgroundColor: "rgba(218,41,28,0.1)",
    border: "1px solid rgba(218,41,28,0.28)",
  },
  priceCellLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: 3,
    fontFamily: FONT.body,
  },
  priceCellAmount: {
    fontSize: 16,
    fontWeight: 800,
    color: C.red,
    lineHeight: 1,
    marginBottom: 3,
    fontFamily: FONT.display,
  },
  priceCellUntil: {
    fontSize: 9,
    color: C.text3,
    fontFamily: FONT.body,
  },
  weightColLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "rgba(96,165,250,0.85)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: 7,
    display: "flex",
    alignItems: "center",
    fontFamily: FONT.body,
  },
  weightPillMale: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: "rgba(59,130,246,0.1)",
    color: "rgba(96,165,250,0.85)",
    borderRadius: 20,
    fontWeight: 600,
    border: "1px solid rgba(59,130,246,0.2)",
    fontFamily: FONT.body,
  },
  weightPillFemale: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: "rgba(236,72,153,0.1)",
    color: "rgba(236,72,153,0.85)",
    borderRadius: 20,
    fontWeight: 600,
    border: "1px solid rgba(236,72,153,0.2)",
    fontFamily: FONT.body,
  },
  registerBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 16px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    width: "100%",
    letterSpacing: "0.4px",
    transition: "opacity 0.15s, transform 0.15s",
  },
  // Mobile variant: slimmer register button
  registerBtnMobile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "9px 12px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    width: "100%",
    letterSpacing: "0.3px",
    transition: "opacity 0.15s, transform 0.15s",
  },

  // ── New horizontal desktop card styles (inserted here) ─────────────────
  deskCard: {
    display: "flex",
    flexDirection: "row" as const,
    backgroundColor: C.surface,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: C.shadow,
    border: `1px solid ${C.border}`,
    transition: "transform 0.25s cubic-bezier(.22,1,.36,1), box-shadow 0.25s",
    width: "100%",
    minWidth: 0,
  },
  deskCardImage: {
    width: 96,
    flexShrink: 0,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: C.surface3,
    position: "relative",
  },
  deskCardImageDim: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
  },
  deskImagePriceBadge: {
    position: "absolute",
    bottom: 10,
    left: 10,
    backgroundColor: "rgba(218,41,28,0.9)",
    backdropFilter: "blur(4px)",
    color: C.onAccent,
    borderRadius: 20,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 700,
    border: "1px solid var(--color-text-subtle)",
    fontFamily: FONT.body,
  },
  deskPlaceholderPriceBadge: {
    display: "inline-flex",
    backgroundColor: C.red,
    color: C.onAccent,
    borderRadius: 20,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 700,
    border: `1px solid ${C.red}`,
    fontFamily: FONT.body,
  },
  deskCardPlaceholder: {
    width: 96,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    position: "relative",
  },
  deskCardBody: {
    flex: 1,
    minWidth: 0,
    padding: "15px 16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  deskCompName: {
    fontFamily: FONT.display,
    fontSize: 20,
    letterSpacing: "0.5px",
    color: C.text,
    lineHeight: 1.2,
    marginBottom: 4,
  },
  deskTypePill: {
    fontSize: 10,
    padding: "2px 10px",
    backgroundColor: "rgba(218,41,28,0.12)",
    color: C.red,
    borderRadius: 20,
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: "1px solid rgba(218,41,28,0.2)",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  deskMetaBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
    marginBottom: 2,
    padding: "8px 0",
  },
  deskMetaLine: {
    display: "grid",
    gridTemplateColumns: "14px minmax(72px, auto) minmax(0, 1fr)",
    alignItems: "start",
    gap: 10,
  },
  deskMetaIcon: {
    color: C.text3,
    flexShrink: 0,
  },
  deskMetaKey: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.6px",
    overflowWrap: "anywhere",
    fontFamily: FONT.body,
  },
  deskMetaVal: {
    fontSize: 12,
    color: C.text,
    fontWeight: 500,
    fontFamily: FONT.body,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  deskDetailsToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: C.text3,
    cursor: "pointer",
    fontFamily: FONT.body,
    alignSelf: "flex-start",
    transition: "border-color 0.15s, color 0.15s",
    marginBottom: 4,
  },
  deskDetailsPanel: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    paddingTop: 4,
  },
  deskDetailLabel: {
    fontSize: 9,
    fontWeight: 800,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    marginBottom: 6,
    fontFamily: FONT.body,
  },
  deskPricingRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap" as const,
  },
  deskPriceCell: {
    flex: 1,
    minWidth: 90,
    padding: "8px 10px",
    backgroundColor: C.surface2,
    borderRadius: 9,
    border: `1px solid ${C.border}`,
  },
  deskPriceCellActive: {
    backgroundColor: "rgba(218,41,28,0.1)",
    border: "1px solid rgba(218,41,28,0.28)",
  },
  deskPriceCellLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: 2,
    fontFamily: FONT.body,
  },
  deskPriceCellAmount: {
    fontSize: 15,
    fontWeight: 800,
    color: C.red,
    lineHeight: 1,
    marginBottom: 2,
    fontFamily: FONT.display,
  },
  deskPriceCellUntil: {
    fontSize: 9,
    color: C.text3,
    fontFamily: FONT.body,
  },
  deskWeightColLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "rgba(96,165,250,0.85)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    fontFamily: FONT.body,
  },
  deskWeightPillMale: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: "rgba(59,130,246,0.1)",
    color: "rgba(96,165,250,0.85)",
    borderRadius: 20,
    fontWeight: 600,
    border: "1px solid rgba(59,130,246,0.2)",
    fontFamily: FONT.body,
  },
  deskWeightPillFemale: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: "rgba(236,72,153,0.1)",
    color: "rgba(236,72,153,0.85)",
    borderRadius: 20,
    fontWeight: 600,
    border: "1px solid rgba(236,72,153,0.2)",
    fontFamily: FONT.body,
  },
  deskRegisterBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 16px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    flex: "0 1 auto",
    width: "auto",
    letterSpacing: "0.4px",
    transition: "opacity 0.15s, transform 0.15s",
    marginTop: 0,
  },

  // ── Footer
  footer: {
    backgroundColor: C.bg,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap" as const,
  },
  footerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  footerText: {
    fontSize: 11,
    color: C.text3,
    fontFamily: FONT.body,
  },
  footerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  footerLink: {
    background: "none",
    border: "none",
    color: C.text3,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: FONT.body,
    padding: 0,
  },
  footerDivider: {
    color: "var(--color-border-strong)",
    fontSize: 11,
  },

  // ── Login modal
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 20,
    backdropFilter: "blur(6px)",
  },
  modalCard: {
    backgroundColor: C.surface,
    borderRadius: 18,
    width: "100%",
    maxWidth: 390,
    overflow: "hidden",
    boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
  },
  modalAccentBar: {
    height: 4,
    background: C.red,
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 22px 12px",
  },
  modalTitle: {
    fontFamily: FONT.display,
    fontSize: 22,
    letterSpacing: "1px",
    color: C.text,
    lineHeight: 1,
  },
  modalSub: {
    fontSize: 12,
    color: C.text2,
    fontFamily: FONT.body,
    marginTop: 2,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: C.text3,
    cursor: "pointer",
    padding: 6,
    display: "flex",
    alignItems: "center",
    borderRadius: 6,
  },
  errorBox: {
    margin: "0 22px 10px",
    padding: "9px 13px",
    backgroundColor: "var(--color-danger-soft)",
    border: "1px solid rgba(218,41,28,0.25)",
    borderRadius: 8,
    fontSize: 13,
    color: C.red,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 500,
    fontFamily: FONT.body,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    marginBottom: 12,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase" as const,
    letterSpacing: "0.8px",
    fontFamily: FONT.body,
  },
  input: {
    padding: "10px 13px",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    color: C.text,
    backgroundColor: C.surface2,
    fontFamily: FONT.body,
    width: "100%",
    boxSizing: "border-box" as const,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputFocus: {
    border: `1px solid #da291c`,
    boxShadow: "0 0 0 3px rgba(218,41,28,0.1)",
    backgroundColor: C.surface,
  },
  submitBtn: {
    width: "100%",
    padding: "12px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 9,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    marginBottom: 4,
  },
  modalFooter: {
    textAlign: "center" as const,
    fontSize: 11,
    color: C.text3,
    padding: "12px 22px 20px",
    fontFamily: FONT.body,
  },
  spinner: {
    width: 14,
    height: 14,
    border: "2px solid var(--color-text-subtle)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },
};
